import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  TextField,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudDownload as ImportIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon,
  Assessment as StatsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Hotel as VacationRentalIcon,
  AccountCircle as AccountIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const SmoobuJobImportManager = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date()); // Today
  const [endDate, setEndDate] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days ahead
  const [importResults, setImportResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importStats, setImportStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountMappings, setAccountMappings] = useState({}); // accountName -> { email, firstName, lastName }
  const [mappingEmailDrafts, setMappingEmailDrafts] = useState({}); // accountName -> draft email string
  const [mappingSavingAccount, setMappingSavingAccount] = useState(null);
  const [mappingError, setMappingError] = useState('');

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  useEffect(() => {
    if (open) {
      fetchImportStats();
      fetchAccounts();
      fetchAccountMappings();
    }
  }, [open]);

  const fetchAccountMappings = async () => {
    try {
      const response = await fetch(`${apiBase}/api/admin/smoobu/account-mapping`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.success) {
        const byAccountName = {};
        const drafts = {};
        (data.mappings || []).forEach(m => {
          byAccountName[m.accountName] = m;
          drafts[m.accountName] = m.email || '';
        });
        setAccountMappings(byAccountName);
        setMappingEmailDrafts(prev => ({ ...drafts, ...prev }));
      }
    } catch (error) {
      console.error('Error fetching Smoobu account mappings:', error);
    }
  };

  const handleSaveAccountMapping = async (accountName) => {
    const email = (mappingEmailDrafts[accountName] || '').trim();
    if (!email) {
      setMappingError('Enter a customer email before saving.');
      return;
    }

    setMappingSavingAccount(accountName);
    setMappingError('');

    try {
      const response = await fetch(`${apiBase}/api/admin/smoobu/account-mapping`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ accountName, email })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save customer mapping');
      }

      await fetchAccountMappings();
    } catch (error) {
      setMappingError(error.message);
    } finally {
      setMappingSavingAccount(null);
    }
  };

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/smoobu/accounts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch accounts');
      }

      const data = await response.json();
      if (data.success) {
        setAccounts(data.accounts || []);
        // Select all accounts by default
        setSelectedAccountIds(data.accounts?.map(a => a.id) || []);
      } else {
        throw new Error(data.error || 'Failed to load accounts');
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      // Don't show error for accounts - just fall back to default behavior
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleAccountToggle = (accountId) => {
    setSelectedAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAllAccounts = () => {
    if (selectedAccountIds.length === accounts.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(accounts.map(a => a.id));
    }
  };

  const fetchImportStats = async () => {
    try {
      const response = await fetch(`${apiBase}/api/admin/smoobu/import-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch import statistics');
      }

      const data = await response.json();
      if (data.success) {
        setImportStats(data.stats);
      } else {
        throw new Error(data.error || 'Failed to load import statistics');
      }
    } catch (error) {
      console.error('Error fetching import stats:', error);
      setError(`Failed to load import statistics: ${error.message}`);
    }
  };

  const handlePreviewImport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after or equal to start date');
      return;
    }

    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      const confirmLargeRange = window.confirm(
        `You've selected a ${daysDiff}-day range. Large date ranges may take longer to process. Do you want to continue?`
      );
      if (!confirmLargeRange) {
        return;
      }
    }

    setPreviewLoading(true);
    setError('');
    setPreviewData(null);

    try {
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';

      const requestUrl = `${apiBase}/api/admin/smoobu/preview-import`;
      const requestBody = {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        tenantId,
        cityCode
      };

      // Include accountIds only if not all accounts are selected
      if (accounts.length > 0 && selectedAccountIds.length > 0 && selectedAccountIds.length < accounts.length) {
        requestBody.accountIds = selectedAccountIds;
      } else if (accounts.length > 0 && selectedAccountIds.length > 0) {
        // All accounts selected - send them all explicitly
        requestBody.accountIds = selectedAccountIds;
      }

      console.log('🔵 [FRONTEND] Making Smoobu preview request');
      console.log('🔵 [FRONTEND] URL:', requestUrl);
      console.log('🔵 [FRONTEND] Body:', requestBody);
      console.log('🔵 [FRONTEND] Token present:', !!localStorage.getItem('token'));

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔵 [FRONTEND] Response status:', response.status);
      console.log('🔵 [FRONTEND] Response OK:', response.ok);

      const data = await response.json();
      console.log('🔵 [FRONTEND] Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to preview import');
      }

      if (data.success) {
        setPreviewData(data.preview);
      } else {
        throw new Error(data.error || 'Failed to preview import');
      }
    } catch (error) {
      setError(`Preview failed: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Bulk-delete every Smoobu-imported job so a fresh import recreates them.
  // Exists because deleting them one by one on the schedule calendar is
  // impractical (past-dated jobs are not even visible there).
  const handleDeleteImportedJobs = async () => {
    const confirmed = window.confirm(
      'Delete ALL Smoobu-imported jobs (every date and account)? Their tasks and assignments are removed too, and re-importing will recreate current reservations from Smoobu. This cannot be undone.'
    );
    if (!confirmed) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setImportResults(null);

    try {
      const response = await fetch(`${apiBase}/api/admin/smoobu/imported-jobs`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to delete imported jobs');
      }
      setSuccess(data.message);
      fetchImportStats();
    } catch (err) {
      console.error('Error deleting imported jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportReservations = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (endDate < startDate) {
      setError('End date must be after or equal to start date');
      return;
    }

    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      const confirmLargeRange = window.confirm(
        `You've selected a ${daysDiff}-day range. Large date ranges may take longer to process. Do you want to continue?`
      );
      if (!confirmLargeRange) {
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setImportResults(null);

    try {
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';

      const requestUrl = `${apiBase}/api/admin/smoobu/import-reservations`;
      const requestBody = {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        tenantId,
        cityCode
      };

      // Include accountIds only if not all accounts are selected
      if (accounts.length > 0 && selectedAccountIds.length > 0 && selectedAccountIds.length < accounts.length) {
        requestBody.accountIds = selectedAccountIds;
      } else if (accounts.length > 0 && selectedAccountIds.length > 0) {
        // All accounts selected - send them all explicitly
        requestBody.accountIds = selectedAccountIds;
      }

      console.log('🟢 [FRONTEND] Making Smoobu import request');
      console.log('🟢 [FRONTEND] URL:', requestUrl);
      console.log('🟢 [FRONTEND] Body:', requestBody);
      console.log('🟢 [FRONTEND] Token present:', !!localStorage.getItem('token'));

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🟢 [FRONTEND] Response status:', response.status);
      console.log('🟢 [FRONTEND] Response OK:', response.ok);

      const data = await response.json();
      console.log('🟢 [FRONTEND] Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import reservations');
      }

      if (data.success) {
        setImportResults(data.results);
        setSuccess(data.message || 'Reservations imported successfully');
        fetchImportStats();
        setPreviewData(null);
      } else {
        throw new Error(data.error || 'Failed to import reservations');
      }
    } catch (error) {
      setError(`Import failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      imported: { color: 'success', icon: <SuccessIcon fontSize="small" /> },
      updated: { color: 'info', icon: <SuccessIcon fontSize="small" /> },
      skipped: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
      error: { color: 'error', icon: <ErrorIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || { color: 'default', icon: null };

    return (
      <Chip
        label={status}
        color={config.color}
        size="small"
        icon={config.icon}
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    // This prevents the UTC midnight = -1 day in local timezone bug
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

    if (dateOnlyPattern.test(dateString)) {
      // Parse as local date by adding time component
      const [year, month, day] = dateString.split('-');
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);

      return localDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    // For full ISO strings with time, use normal parsing
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <VacationRentalIcon color="primary" />
          Smoobu Reservation Import Manager
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Import Statistics */}
          {importStats && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <StatsIcon color="primary" />
                    <Typography variant="h6">Import Statistics</Typography>
                    <Tooltip title="Refresh Statistics">
                      <IconButton size="small" onClick={fetchImportStats}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Total Imported
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {importStats.totalImported || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Last Import
                      </Typography>
                      <Typography variant="body1">
                        {importStats.lastImportDate
                          ? formatDate(importStats.lastImportDate)
                          : 'Never'
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Date Range Selection */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Import Date Range
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a date range to import reservations from Smoobu. The system will create cleaning jobs based on checkout times.
              </Typography>

              {/* Import Tips */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Import Tips:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Upcoming reservations:</strong> Import future checkouts to schedule cleanings
                  <br />
                  • <strong>Current reservations:</strong> Import today's and recent checkouts
                  <br />
                  • <strong>Automatic jobs:</strong> Cleaning jobs are created at checkout time
                  <br />
                  • <strong>Property matching:</strong> Apartments are matched by address
                </Typography>
              </Alert>

              {/* Account Selection */}
              {accounts.length > 1 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccountIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2">
                      Select Smoobu Accounts to Import From
                    </Typography>
                  </Box>
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedAccountIds.length === accounts.length}
                            indeterminate={selectedAccountIds.length > 0 && selectedAccountIds.length < accounts.length}
                            onChange={handleSelectAllAccounts}
                          />
                        }
                        label={<Typography variant="body2" fontWeight="bold">Select All</Typography>}
                      />
                      {accounts.map(account => (
                        <FormControlLabel
                          key={account.id}
                          control={
                            <Checkbox
                              checked={selectedAccountIds.includes(account.id)}
                              onChange={() => handleAccountToggle(account.id)}
                            />
                          }
                          label={<Typography variant="body2">{account.name}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                  {selectedAccountIds.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Please select at least one account to import from.
                    </Alert>
                  )}
                </Box>
              )}
              {accounts.length === 1 && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccountIcon color="primary" fontSize="small" />
                    <Typography variant="body2">
                      Importing from: <strong>{accounts[0].name}</strong>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Real customer mapping per Smoobu account */}
              {accounts.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Billed Customer per Smoobu Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Imported jobs use the vacation-rental guest as contact info only.
                    Set the real PrimeShine customer each account should bill to below
                    (the customer must already exist in the system).
                  </Typography>
                  {mappingError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMappingError('')}>
                      {mappingError}
                    </Alert>
                  )}
                  {accounts.map(account => {
                    const mapping = accountMappings[account.name];
                    return (
                      <Box
                        key={account.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 180 }}>
                          {account.name}
                        </Typography>
                        <TextField
                          size="small"
                          placeholder="customer@example.com"
                          value={mappingEmailDrafts[account.name] ?? ''}
                          onChange={(e) => setMappingEmailDrafts(prev => ({
                            ...prev,
                            [account.name]: e.target.value
                          }))}
                          sx={{ flexGrow: 1, maxWidth: 320 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={mappingSavingAccount === account.name}
                          onClick={() => handleSaveAccountMapping(account.name)}
                        >
                          {mappingSavingAccount === account.name ? 'Saving...' : 'Save'}
                        </Button>
                        {mapping && (
                          <Chip
                            size="small"
                            color="success"
                            label={`Mapped: ${mapping.firstName || ''} ${mapping.lastName || ''}`.trim() || mapping.email}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Quick Date Presets */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Quick presets:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      setStartDate(today);
                      setEndDate(today);
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                      setStartDate(today);
                      setEndDate(nextWeek);
                    }}
                  >
                    Next 7 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const nextMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
                      setStartDate(today);
                      setEndDate(nextMonth);
                    }}
                  >
                    Next 30 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const today = new Date();
                      const next3Months = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
                      setStartDate(today);
                      setEndDate(next3Months);
                    }}
                  >
                    Next 90 Days
                  </Button>
                </Box>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      minDate={startDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreviewImport}
                        disabled={previewLoading || loading || (accounts.length > 0 && selectedAccountIds.length === 0)}
                        fullWidth
                      >
                        Preview
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<ImportIcon />}
                        onClick={handleImportReservations}
                        disabled={loading || previewLoading || (accounts.length > 0 && selectedAccountIds.length === 0)}
                        fullWidth
                      >
                        Import
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleDeleteImportedJobs}
                        disabled={loading || previewLoading}
                        fullWidth
                      >
                        Delete Imported
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </LocalizationProvider>

              {/* Date validation message */}
              {startDate && endDate && startDate > endDate && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Start date should be before or equal to end date.
                </Alert>
              )}

              {/* Date range info */}
              {startDate && endDate && startDate <= endDate && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Selected Range:</strong> {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
                    {' '}({Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} days)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Loading */}
          {(loading || previewLoading) && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {loading ? 'Importing reservations...' : 'Loading preview...'}
              </Typography>
            </Grid>
          )}

          {/* Messages */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* Preview Results */}
          {previewData && (
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Import Preview - {previewData.totalReservations} reservations found
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Total Reservations
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {previewData.totalReservations}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        New Jobs
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {previewData.newJobs || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Updates
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {previewData.jobsToUpdate || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        No Changes
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {previewData.noChangeJobs || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reservation ID</TableCell>
                          {accounts.length > 1 && <TableCell>Account</TableCell>}
                          <TableCell>Apartment</TableCell>
                          <TableCell>Guest Name</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>Checkout Date</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.reservations?.slice(0, 10).map((reservation) => {
                          const getActionChip = (actionType) => {
                            switch (actionType) {
                              case 'imported':
                                return <Chip label="Create New" color="success" size="small" />;
                              case 'updated':
                                return <Chip label="Update" color="info" size="small" />;
                              case 'skipped':
                                return <Chip label="No Changes" color="default" size="small" />;
                              case 'error':
                                return <Chip label="Error" color="error" size="small" />;
                              default:
                                return <Chip label="Unknown" color="default" size="small" />;
                            }
                          };

                          const formatAddress = (address) => {
                            if (!address) return '-';
                            const parts = [];
                            if (address.street) parts.push(address.street);
                            if (address.city) parts.push(address.city);
                            if (address.state) parts.push(address.state);
                            if (address.zip) parts.push(address.zip);
                            return parts.length > 0 ? parts.join(', ') : '-';
                          };

                          return (
                            <TableRow key={reservation.smoobuReservationId}>
                              <TableCell>{reservation.smoobuReservationId}</TableCell>
                              {accounts.length > 1 && (
                                <TableCell>
                                  <Chip
                                    label={reservation.accountName || '-'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                {reservation.apartmentName || `Apartment ${reservation.apartmentId}`}
                              </TableCell>
                              <TableCell>{reservation.guestName || '-'}</TableCell>
                              <TableCell>{formatAddress(reservation.address)}</TableCell>
                              <TableCell>
                                {reservation.checkoutDate ? formatDate(reservation.checkoutDate) : '-'}
                              </TableCell>
                              <TableCell>
                                {getActionChip(reservation.actionType)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {previewData.reservations && previewData.reservations.length > 10 && (
                    <Typography variant="body2" sx={{ mt: 1 }} color="textSecondary">
                      Showing first 10 of {previewData.reservations.length} reservations
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Import Results */}
          {importResults && (
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Import Results - {importResults.summary.totalFetched} reservations processed
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Total Fetched
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {importResults.summary.totalFetched}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Imported
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {importResults.summary.imported}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Updated
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {importResults.summary.updated || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Skipped
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {importResults.summary.skipped}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <Typography variant="body2" color="textSecondary">
                        Errors
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        {importResults.summary.errors}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Reservation ID</TableCell>
                          {accounts.length > 1 && <TableCell>Account</TableCell>}
                          <TableCell>Apartment</TableCell>
                          <TableCell>Guest Name</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.details?.map((detail, index) => {
                          const formatAddress = (address) => {
                            if (!address) return '-';
                            const parts = [];
                            if (address.street) parts.push(address.street);
                            if (address.city) parts.push(address.city);
                            if (address.state) parts.push(address.state);
                            if (address.zip) parts.push(address.zip);
                            return parts.length > 0 ? parts.join(', ') : '-';
                          };

                          return (
                            <TableRow key={index}>
                              <TableCell>{detail.smoobuReservationId}</TableCell>
                              {accounts.length > 1 && (
                                <TableCell>
                                  <Chip
                                    label={detail.accountName || '-'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                {detail.apartmentName || `Apartment ${detail.apartmentId}`}
                              </TableCell>
                              <TableCell>{detail.guestName || '-'}</TableCell>
                              <TableCell>{formatAddress(detail.address)}</TableCell>
                              <TableCell>{getStatusChip(detail.status)}</TableCell>
                              <TableCell>{detail.message}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmoobuJobImportManager;
